import { useUserStore } from '../../../store/useUserStore';
import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TextField } from '@mui/material';
import { useKidDetailQuery, useCreateQnaMutation } from '../../../queries';
import {
  SELECT_STYLE,
  COMMUNITY_QNA_TYPE,
  COMMUNITY_USER_TYPE,
  COMMUNITY_TYPE,
} from '../../../data/define';
import { BtnCss } from '../../../utility/css';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import toast from 'react-hot-toast';
import moment from 'moment';
import CustomSwitch from '../../common/CustomSwitch';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FullHeightContainer from '../../common/FullHeightContainer';
import TopHeaderBar from './TopHeaderBar';
import BottomFixedBtn from '../../common/BottomFixedBtn';

interface AnalysisProps {
  type: number;
  setWriteModalOpen: (open: boolean) => void;
  onClose?: () => void;
}

interface ReportItem {
  id: number;
  createdAt: string;
}

export default function Analysis({ type, setWriteModalOpen, onClose }: AnalysisProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useUserStore();
  const [registorDisabled, setRegistorDisabled] = useState(false);
  const [reports, setReports] = useState<ReportItem[]>([]);
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);
  const [userType, setUserType] = useState<
    (typeof COMMUNITY_USER_TYPE)[keyof typeof COMMUNITY_USER_TYPE]
  >(COMMUNITY_USER_TYPE.USER);
  const [isHidden, setIsHidden] = useState(false);
  const [content, setContent] = useState('');

  const { data: kidDetail } = useKidDetailQuery(user?.mainKid?.id as number, {
    enabled: !!user?.mainKid?.id,
  });

  useEffect(() => {
    if (kidDetail) {
      if (kidDetail.reports.length < 1) {
        toast.error('리포트가 없습니다. 리포트를 생성해주세요.');
        return;
      }
      setReports(kidDetail.reports || []);
      setSelectedReportId(kidDetail.reports?.[0]?.id || null);
    }
  }, [kidDetail]);

  useEffect(() => {
    setRegistorDisabled(content.length < 1);
  }, [content]);

  const { mutate: createQna } = useCreateQnaMutation();

  const handleCreateQna = useCallback(() => {
    const apiParams = {
      content,
      questionType: COMMUNITY_QNA_TYPE.ANALYSIS,
      userType,
      kidId: user?.mainKid?.id,
      isPrivate: isHidden,
      reportId: selectedReportId,
    };
    createQna(apiParams, {
      onSuccess: () => {
        if (onClose) {
          onClose();
        } else {
          navigate(-1);
        }
      },
      onError: (error) => {
        console.error('Error creating QnA:', error);
        toast.error('Failed to create QnA');
      },
    });
  }, [content, userType, user, isHidden, selectedReportId, navigate, onClose, createQna]);

  return (
    <FullHeightContainer>
      <TopHeaderBar
        type={type}
        setWriteModalOpen={setWriteModalOpen}
        registorDisabled={registorDisabled}
        onSubmit={handleCreateQna}
        onClose={onClose}
      />
      <div className="pageInner">
        <div className="w-full text-[#1C1C1C] font-[700] text-[15px] text-left mt-[20px]">
          리포트
        </div>
        <div className="w-full flex items-center justify-between gap-2 mt-[5px]">
          <div className="flex items-center justify-start">
            {reports.length > 0 && (
              <Select
                variant="outlined"
                value={selectedReportId || ''}
                onChange={(e) => {
                  const val = e.target.value as number;
                  setSelectedReportId(val);
                  navigate(
                    { pathname: location.pathname, search: `?reportId=${val}` },
                    { replace: true }
                  );
                }}
                renderValue={(selected) => (
                  <>
                    <span style={{ color: '#969696', fontWeight: 600 }}>조회일&nbsp;</span>
                    <span style={{ color: '#363636', fontWeight: 700 }}>
                      {reports.length > 0 ? (
                        <>
                          {moment(
                            reports.find((report) => report.id === selected)?.createdAt
                          ).format('YY.MM.DD') || '선택된 리포트 없음'}
                        </>
                      ) : (
                        <>...</>
                      )}
                    </span>
                  </>
                )}
                IconComponent={KeyboardArrowDownIcon}
                sx={{ ...SELECT_STYLE, backgroundColor: '#F0FCFC' }}
              >
                {reports?.map((report) => (
                  <MenuItem key={report.id} value={report.id} sx={{ fontSize: '14px' }}>
                    <span className="text-[14px] text-[#363636]">
                      {moment(report.createdAt).format('YYYY.MM.DD')}
                    </span>
                  </MenuItem>
                ))}
              </Select>
            )}
          </div>
          <div className="flex items-center justify-center gap-2">
            <button
              type="button"
              className="px-3 py-2"
              style={userType === COMMUNITY_USER_TYPE.USER ? BtnCss.Focused : BtnCss.Blured}
              onClick={() => setUserType(COMMUNITY_USER_TYPE.USER)}
            >
              사용자
            </button>
            <button
              type="button"
              className="px-3 py-2"
              style={userType === COMMUNITY_USER_TYPE.EXPERT ? BtnCss.Focused : BtnCss.Blured}
              onClick={() => setUserType(COMMUNITY_USER_TYPE.EXPERT)}
            >
              전문가
            </button>
          </div>
        </div>
        <div className="w-full mt-[30px]">
          <div className="flex justify-between mb-1 items-center h-[30px] gap-2">
            <div className="font-[700] text-[16px] text-[#1C1C1C] text-left">내용</div>
            <div
              className="flex items-center justify-center gap-2"
              style={{
                display: userType === COMMUNITY_USER_TYPE.EXPERT ? 'flex' : 'none',
              }}
            >
              <CustomSwitch
                value={isHidden}
                label="비공개"
                onToggle={() => setIsHidden(!isHidden)}
              />
            </div>
          </div>
          <div className="w-full relative mb-[12px]">
            <TextField
              placeholder="내용을 작성해 주세요"
              variant="outlined"
              fullWidth
              multiline
              rows={10}
              value={content}
              onChange={(e) => {
                if (e.target.value.length < 301) {
                  setContent(e.target.value);
                }
              }}
              sx={{ mb: 2 }}
            />
            <div className="absolute right-2 bottom-5 text-[12px] text-[#767676]">
              {content.length}/300
            </div>
          </div>
        </div>
      </div>
      <BottomFixedBtn
        title={type === COMMUNITY_TYPE.FREE_BOARD ? '작성 완료' : '등록'}
        onClick={handleCreateQna}
        disabled={registorDisabled}
      />
    </FullHeightContainer>
  );
}
